/**
 * Objective scoring, pure: physics sensors report objects entering and leaving a drop
 * zone, or the player crossing the maze's exit; this module folds those events into
 * progress. No polling, no positions — just discrete events against the objective's
 * requirements.
 *
 * The two event paths are deliberately separate rather than one event with a "who"
 * field. What completes a `deposit-objects` objective is *which* objects are in the
 * zone; what completes a `reach-exit` one is the player arriving at all. Keeping them
 * apart is what makes a box rolling out of the maze not the same thing as escaping it.
 */

import type { LevelSpec, ObjectiveSpec } from './profile/types';

export type ObjectiveProgress = {
  objectiveId: string;
  description: string;
  requiredIds: readonly string[];
  /** Required objects currently inside the zone. */
  depositedIds: readonly string[];
  /**
   * Latches: once everything has been in the zone together, the objective stays
   * completed even if a box is later carried back out. Undoing a player's win over a
   * physics wobble would be crueller than letting the edge case stand.
   */
  isComplete: boolean;
};

export type ZoneEvent = { type: 'enter' | 'leave'; objectId: string };

export function initialObjectiveProgress(spec: ObjectiveSpec): ObjectiveProgress {
  return {
    objectiveId: spec.id,
    description: spec.description,
    // Nothing to carry out of a maze: reaching the exit is the whole of it.
    requiredIds: spec.kind === 'deposit-objects' ? spec.objectIds : [],
    depositedIds: [],
    isComplete: false,
  };
}

export function applyZoneEvent(progress: ObjectiveProgress, event: ZoneEvent): ObjectiveProgress {
  if (!progress.requiredIds.includes(event.objectId)) return progress;

  const isDeposited = progress.depositedIds.includes(event.objectId);
  if (event.type === 'enter' && isDeposited) return progress;
  if (event.type === 'leave' && !isDeposited) return progress;

  const depositedIds =
    event.type === 'enter'
      ? [...progress.depositedIds, event.objectId]
      : progress.depositedIds.filter((id) => id !== event.objectId);

  return {
    ...progress,
    depositedIds,
    isComplete: progress.isComplete || depositedIds.length === progress.requiredIds.length,
  };
}

/**
 * The player crossed the objective's own zone — the gap the maze opens onto the far
 * apron. Latching like `applyZoneEvent`, and returning the same object when nothing
 * changed, so a player wandering in and out again does not re-render the HUD.
 */
export function applyPlayerEnterZone(progress: ObjectiveProgress): ObjectiveProgress {
  if (progress.isComplete) return progress;
  return { ...progress, isComplete: true };
}

/**
 * Adds an initial record for each of the level's objectives that has none. An existing
 * record is never overwritten (a revisited level keeps its completed objective), and the
 * same object comes back when nothing was added.
 */
export function seedObjectives(
  progress: Record<string, ObjectiveProgress>,
  level: LevelSpec,
): Record<string, ObjectiveProgress> {
  const missing = level.objectives.filter((spec) => !(spec.id in progress));
  if (missing.length === 0) return progress;
  const next = { ...progress };
  for (const spec of missing) next[spec.id] = initialObjectiveProgress(spec);
  return next;
}
